<template>
  <div :class="filtered ? 'container-fluid product-wrapper sidebaron' : 'container-fluid product-wrapper'">
    <div class="product-grid">

      <div class="feature-products">
        <BaseFilter
          screenKey="order-list-page"
          :model-value="allParams"
          @update:model-value="val => Object.assign(allParams, val)"
          :fields="filterConfig"
          @search="handleSearch"
          @reset="handleReset"
        />
      </div>

      <div class="p-4 bg-white mt-3 shadow-sm rounded">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h3 class="m-0">Order List</h3>
          <div class="table-tools">
            <button v-if="selectedIds.length > 0" class="btn btn-sm btn-info me-1 text-white" @click="onBuyTracking">
              <i class="fa fa-shopping-cart me-1"></i> Buy Tracking
            </button>

            <button v-if="selectedIds.length > 0" class="btn btn-sm btn-info me-1 text-white" @click="onPrintLabel">
              <i class="fa fa-print me-1"></i> Print Label
            </button>
            <button v-if="selectedIds.length > 0 && isAdmin" class="btn btn-sm btn-outline-primary me-1" @click="isChangeStatusOpen = true">
              <i class="fa fa-refresh me-1"></i> Change Status
            </button>
            <button v-if="selectedIds.length > 0 && !isSale" class="btn btn-sm btn-danger me-1" @click="onClickCancel">

              <i class="fa fa-times me-1"></i> Cancel
            </button>
            <button v-if="selectedIds.length > 0 && !isSale" class="btn btn-sm btn-success me-1" @click="onPay" :disabled="isPaying">
              <span v-if="isPaying" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <i v-else class="fa fa-dollar me-1"></i> Pay
            </button>
            <button class="btn btn-sm btn-success me-1" @click="isExportOpen = true">
              <i class="fa fa-download me-1"></i> Export
            </button>
            <button class="btn btn-sm btn-info text-white me-1" @click="onImport">
              <i class="fa fa-upload me-1"></i> Import
            </button>
            <button class="btn btn-sm btn-outline-secondary me-1" @click="fetchData">
              <i class="fa fa-refresh me-1"></i> Refresh
            </button>
            <router-link v-if="!isSale" to="/orders/add">
                <button class="btn btn-sm btn-primary">
                    <i class="fa fa-plus me-1"></i> Add
                </button>
            </router-link>
          </div>

        </div>

        <BaseTable
          ref="tableRef"
          :columns="tableCols"
          :data="tableData"
          :loading="loading"
          :total-items="total"
          v-model:current-page="allParams.page"
          v-model:page-size="allParams.limit"
          storage-key="orders"
          show-checkbox
          @sort="handleSort"
          @selection-change="handleSelection"
        >
          <template #cell(no)="{ index }">
            <span>{{ rowOffset + index + 1 }}</span>
          </template>
          <template #cell(code)="{ item }">
            <div class="d-flex flex-column py-1 line-height-base">
              <span v-if="item.name" class="fw-bold">{{ item.name }}</span>
              <div class="d-flex align-items-center gap-1">
                <span>{{ item.code }}</span>
                <button
                  class="btn btn-sm btn-outline-primary py-0 px-1"
                  style="font-size: 0.7rem;"
                  @click="copyToClipboard(item.code)"
                  title="Copy code"
                >
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
            </div>
          </template>
          <template #cell(status)="{ item }">
            <span :class="item.status === 'Active' ? 'badge bg-success' : 'badge bg-secondary'">
              {{ item.status }}
            </span>
          </template>
          <template #cell(service_id)="{ item }">
            <span>
              {{ getServiceName(item.service_id) }}
            </span>
          </template>
          <template #cell(status_id)="{ item }">
            <span class="status-badge" :class="getStatusClass(item.status_id)">
              {{ getStatusName(item.status_id) }}
            </span>
          </template>
          <template #cell(buy_tracking_status_id)="{ item }">
            <span v-if="getBuyTrackingStatusName(item.buy_tracking_status_id)" class="status-badge" :class="getBuyTrackingStatusClass(item.buy_tracking_status_id)">
              {{ getBuyTrackingStatusName(item.buy_tracking_status_id) }}
            </span>
          </template>
          <template #cell(total_order_fee)="{ item }">
            <span>
              {{ Number(item.total_order_fee).toLocaleString() }}
            </span>
          </template>
          <template #cell(customer_id)="{ item }">
            <span>
              {{ item.customer?.email ?? '' }}
            </span>
          </template>
          <template #cell(customer_email)="{ item }">
            <span>
              {{ item.customer?.email ?? '' }}
            </span>
          </template>

          <template #cell(receiver_country_id)="{ item }">
            <span>
              {{ getCountryName(item.receiver_country_id) }}
            </span>
          </template>
          <template #cell(remain_fee)="{ item }">
            <span :class="{ 'text-danger fw-bold': item.remain_fee > 0 }">
              {{ Number(item.remain_fee || 0).toLocaleString() }}
            </span>
          </template>
          <template #cell(created_at)="{ item }">
            <div class="d-flex flex-column py-1 line-height-base">
              <span>Created: {{ formatDateTime(item.created_at) }}</span>
              <span>Departed: {{ formatDateTime(item.departed_date) }}</span>
              <span>Delivered: {{ formatDateTime(item.delivered_date) }}</span>
            </div>
          </template>
          <template #cell(tracking_number)="{ item }">
            <div v-if="isTrackingHiddenForCustomer(item)" class="text-danger small fw-bold">
              <i class="fa fa-lock me-1"></i> Thanh toán để xem
            </div>
            <div v-else class="d-flex align-items-center gap-1">
              <span>{{ item.tracking_number }}</span>
              <button
                v-if="item.tracking_number"
                class="btn btn-sm btn-outline-primary py-0 px-1"
                style="font-size: 0.7rem;"
                @click="copyToClipboard(item.tracking_number)"
                title="Copy tracking number"
              >
                <i class="bi bi-clipboard"></i> Copy
              </button>
            </div>
          </template>
          <template #cell(receive_date)="{ item }">
            <div class="d-flex flex-column py-1 line-height-base">
              <span>{{ formatDateTime(item.receive_date) }}</span>
            </div>
          </template>
          <template #actions="{ item }">
            <button class="btn btn-sm btn-info text-white me-1" title="Xem chi tiết" @click="viewDetail(item)">
              <i class="fa fa-eye"></i>
            </button>
            <button class="btn btn-sm btn-outline-info me-1" title="In Label" @click="printItem(item)">
              <i class="fa fa-print"></i>
            </button>

            <button class="btn btn-sm btn-primary me-1" @click="editItem(item)">

              <i class="fa fa-edit"></i>
            </button>
            <button
              v-if="isAdmin && Number(item.remain_fee) < -0.01"
              class="btn btn-sm btn-outline-warning me-1"
              :disabled="resyncingOrderId !== null"
              aria-label="Đồng bộ lại thanh toán đơn hàng"
              title="Đồng bộ lại thanh toán và hoàn phần dư vào số dư Vio"
              @click="resyncOrderPayment(item)"
            >
              <span v-if="String(resyncingOrderId) === String(item.id)" class="spinner-border spinner-border-sm" role="status"></span>
              <i v-else class="fa fa-refresh"></i>
            </button>
            <button class="btn btn-sm btn-outline-primary" title="Thanh toán" @click="payItem(item)">
              <i class="fa fa-dollar"></i>
            </button>

          </template>
        </BaseTable>

        <!-- Tổng tiền chưa thanh toán của các đơn đã chọn -->
        <div v-if="selectedIds.length > 0 && totalUnpaidSelected > 0" class="mt-3 p-3 bg-light border border-danger rounded d-flex justify-content-end align-items-center shadow-sm">
          <span class="fw-bold me-3 text-secondary">Tổng số tiền chưa thanh toán (đã chọn):</span>
          <span class="h4 m-0 text-danger fw-bold">{{ totalUnpaidSelected.toLocaleString() }} đ</span>
        </div>
      </div>
    </div>
    <ConfirmOrder
      :isOpen="isConfirmOrder"
      :type="typeConfirm"
      :selectedIds="selectedIds"  @close="isConfirmOrder = false"
      @confirm="confirmModal"
    />
    <ImportOrder
      :isOpen="isImportOrder"
      @close="isImportOrder = false"
      @success="fetchData"
    />
    <OrderDetail
      :isOpen="isOrderDetailOpen"
      :order="selectedOrder"
      :services="services"
      :statuses="statusOrder"
      :countries="countries"
      @close="isOrderDetailOpen = false"
    />
    <OrderEditModal
      :isOpen="isOrderEditOpen"
      :orderId="editingOrderId"
      @close="isOrderEditOpen = false"
      @save-success="fetchData"
    />
    <ChangeStatusModal
      :isOpen="isChangeStatusOpen"
      :selectedIds="selectedIds"
      :statuses="statusOrder"
      @close="isChangeStatusOpen = false"
      @confirm="confirmBatchStatus"
    />
    <ExportOrderModal
      :isOpen="isExportOpen"
      :filterParams="allParams"
      :selectedIds="selectedIds"
      @close="isExportOpen = false"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue';
import BaseFilter from "../../common/BaseFilter.vue";
import BaseTable from "../../common/BaseTable.vue";
import { useOrders } from "@/composables/useOrders";
import { createToaster } from "@meforma/vue-toaster";
import { useRouter } from 'vue-router';
import {useService} from "@/composables/useService";
import {useStatus} from "@/composables/useStatus";
import {usePayments} from "@/composables/usePayments";
import {useCurrentUser} from "@/composables/useCurrentUser";
import { canViewTrackingBeforePayment } from "@/constants/trackingVisibility";
import {useCountry} from "@/composables/useCountry";
import {useUser} from "@/composables/useUser";
import ConfirmOrder from '../modal/confirmOrder.vue'


import ImportOrder from '../modal/importOrder.vue'
import OrderDetail from '../modal/orderDetailModal.vue'
import OrderEditModal from '../modal/orderEditModal.vue'
import ChangeStatusModal from '../modal/changeStatusModal.vue'
import ExportOrderModal from '../modal/exportOrderModal.vue'

const router = useRouter();
const toaster = createToaster({
  position: "top-right",
  duration: 2000,
});
const { getServicesApi } = useService();
const { getStatusesApi } = useStatus();
const { payOrdersApi } = usePayments();
const { currentUser, loadCurrentUser } = useCurrentUser();
const { fetchOrdersApi, changeStatusApi, resyncOrderPaymentApi, buyTrackingApi, downloadLabelsApi } = useOrders();
const { getCountriesApi } = useCountry();
const { getUsersApi } = useUser();

const isAdmin = computed(() => {
  return currentUser.value?.roles?.some(role => {
    const name = role.name.toLowerCase();
    return name === 'admin' || name === 'administrator';
  });
});

const isCustomer = computed(() => {
  return currentUser.value?.roles?.some(role => role.name.toLowerCase() === 'customer');
});

const isSale = computed(() => {
  return currentUser.value?.roles?.some(role => role.name.toLowerCase() === 'sale');
});


// Các dịch vụ nhập tracking thủ công luôn hiển thị tracking cho khách hàng.
const MANUAL_TRACKING_SERVICE_IDS = [8, 9];

const isTrackingHiddenForCustomer = (order) =>
  isCustomer.value
  && Number(order.remain_fee) > 0
  && !MANUAL_TRACKING_SERVICE_IDS.includes(Number(order.service_id))
  && !canViewTrackingBeforePayment(currentUser.value?.email);

const filtered = ref(false);
const loading = ref(false);
const isPaying = ref(false);
const resyncingOrderId = ref(null);
const isConfirmOrder = ref(false);
const isImportOrder = ref(false);
const isOrderDetailOpen = ref(false);
const isOrderEditOpen = ref(false);
const isChangeStatusOpen = ref(false);
const isExportOpen = ref(false);
const editingOrderId = ref(null);
const selectedOrder = ref(null);
const tableData = ref([]);
const rowOffset = ref(0);
const total = ref(0);
const services = ref([]);
const countries = ref([]);
const statusOrder = ref([]);
const statusBuyTracking = ref([]);
const sales = ref([]);
const selectedIds = ref([]);
const tableRef = ref(null);
const typeConfirm = ref('');

const allParams = reactive({
  page: 1,
  limit: 10,
  orderBy: 'id',
  sortedBy: 'desc',
  searchJoin: 'AND',
  include: 'customer,order_items,fees,expenses',
  searchFields: '',
  search: '',
});
const filterConfig = computed(() => [
  {
    key: 'name',
    type: 'text',
    label: 'Order Name',
    operator: 'like',
    placeholder: 'Tìm theo tên đơn...',
    isDefault: true
  },
  {
    key: 'code',
    type: 'text',
    operator: 'like',
    label: 'Order Code',
    placeholder: 'Mã đơn hàng',
    isDefault: true
  },
  {
    key: 'tracking_number',
    type: 'text',
    operator: 'like',
    label: 'Tracking Number',
    placeholder: 'Mã theo dõi',
    isDefault: true
  },
  {
    key: 'status_id',
    type: 'select',
    operator: '=',
    label: 'Status',
    isDefault: true,
    options: statusOrder.value.map(item => ({
      label: item.display_name,
      value: item.id
    }))
  },

  // --- NHÓM ĐỐI TÁC & VẬN CHUYỂN ---
  {
    key: 'customer.email',
    type: 'text',
    label: 'Customer Email',
    operator: 'like',
    placeholder: 'Email khách hàng'
  },
  {
    key: 'tracking_master',
    type: 'text',
    operator: 'like',
    label: 'Tracking Master',
    placeholder: 'Mã Master'
  },
  {
    key: 'service_id',
    type: 'select',
    operator: '=',
    label: 'Services',
    options: services.value.map(item => ({
      label: item.name,
      value: item.id
    }))
  },
  {
    key: 'receiver_country_id',
    type: 'multiselect',
    operator: '=',
    label: 'Receiver Country',
    placeholder: 'Tìm quốc gia...',
    options: [
      { label: '--- Tất cả ---', value: '' },
      ...countries.value.map(item => ({
        label: item.display_name,
        value: item.id
      }))
    ]
  },

  // --- NHÓM THỜI GIAN ---
  {
    type: 'daterange',
    fromKey: 'departed_date_from',
    toKey: 'departed_date_to',
    searchField: 'departed_date',
    label: 'Ngày Depart',
    isDefault: true
  },
  {
    type: 'daterange',
    fromKey: 'delivered_date_from',
    toKey: 'delivered_date_to',
    searchField: 'delivered_date',
    label: 'Ngày Delivered',
    isDefault: true
  },
  {
    type: 'daterange',
    fromKey: 'received_date_from',
    toKey: 'received_date_to',
    searchField: 'received_date',
    label: 'Ngày Receive',
    isDefault: true
  },
  {
    type: 'daterange',
    fromKey: 'created_date_from',
    toKey: 'created_date_to',
    searchField: 'date_create_order',
    label: 'Ngày tạo đơn',
    isDefault: true
  },
  {
      key: 'show_order_canceled',
      type: 'checkbox',
      label: 'Canceled Orders',
      checkboxLabel: 'Show canceled orders',
      isDefault: true
  },

  // --- NHÓM TRẠNG THÁI NỘI BỘ ---
  ...((isAdmin.value || isCustomer.value) ? [{
    key: 'has_remain_fee',
    type: 'checkbox',
    label: 'Unpaid Fee',
    checkboxLabel: 'Unpaid Fee',
    isDefault: true
  }] : []),
  {
    key: 'buy_tracking_status_id',
    type: 'select',
    operator: '=',
    label: 'Buy Tracking Status',
    options: statusBuyTracking.value.map(item => ({
      label: item.display_name,
      value: item.id
    }))
  },
  {
    key: 'clearance_status_id',
    type: 'select',
    operator: '=',
    label: 'Clearance Status',
    options: [
      { label: 'Chờ thông quan', value: 1 },
      { label: 'Đang kiểm hoá', value: 2 },
      { label: 'Đã thông quan', value: 3 },
    ]
  },
  {
    key: 'internal_warehouse',
    type: 'select',
    operator: '=',
    label: 'Internal Warehouse',
    options: [
      { label: 'Kho HN', value: 'HN' },
      { label: 'Kho HCM', value: 'HCM' }
    ]
  },
  ...(isAdmin.value ? [{
    key: 'sale_id',
    type: 'select',
    operator: '=',
    label: 'Collaborator',
    options: sales.value.map(item => ({
      label: item.name,
      value: item.id
    }))
  }] : []),

  // --- NHÓM CHECK NHIỀU (Nên ẩn đi, chỉ hiện khi cần) ---
  // Keys map to OrderCriteria's applyTrackingSearch (arr_tracking) and applyNameSearch (arr_name).
  // Comma-separated values are split server-side into LIKE queries.
  {
    key: 'arr_tracking',
    type: 'textarea',
    operator: '=',
    label: 'Multi Tracking Check',
    placeholder: 'Mã 1\nMã 2\n...'
  },
  {
    key: 'arr_name',
    type: 'textarea',
    operator: '=',
    label: 'Multi Name Check',
    placeholder: 'Tên 1\nTên 2\n...'
  }
]);

const tableCols = [
  { key: 'no', label: 'No', width: '60px' },
  { key: 'code', label: 'Order Code', sortable: true },
  { key: 'customer_id', label: 'Customer' },

  { key: 'receiver_country_id', label: 'Receiver Country' },
  { key: 'service_id', label: 'Service' },
  { key: 'status_id', label: 'Status' },
  { key: 'buy_tracking_status_id', label: 'Buy Tracking Status' },
  { key: 'tracking_number', label: 'Tracking Number' },
  { key: 'total_order_fee', label: 'Total Order Fee' },
  { key: 'remain_fee', label: 'Remain Fee' },
  { key: 'receive_date', label: 'Receive Date' },
  { key: 'created_at', label: 'Trace Date' },
];
const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);

  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();

  const h = String(date.getHours()).padStart(2, '0');
  const i = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');

  return `${d}/${m}/${y} ${h}:${i}:${s}`;
};

const getServiceName = (id) => {
  const service = services.value.find(s => s.id === id);
  return service ? service.name : 'Không xác định';
};

const getCountryName = (id) => {
    const country = countries.value.find(c => c.id === id);
    return country ? country.display_name : 'Không xác định';
};

const getStatusName = (id) => {
  const status = statusOrder.value.find(s => s.id === id);
  return status ? status.display_name : 'Không xác định';
};

const getBuyTrackingStatusName = (id) => {
  const status = statusBuyTracking.value.find(s => s.id === id);
  return status ? status.display_name : '';
};

const getBuyTrackingStatusClass = (id) => {
  const map = {
    27: 'status-default',    // Draft
    28: 'status-new',        // Chờ mua
    29: 'status-done',       // Mua thành công
    30: 'status-cancel',     // Mua thất bại
    31: 'status-conf',       // Sẵn sàng mua
    32: 'status-transmit',   // Đang mua
    33: 'status-wh',         // Đang xử lý mua
    34: 'status-loss',       // Không hợp lệ
  };
  return map[id] || 'status-default';
};

const getStatusClass = (id) => {
  const map = {
    10: 'status-new',          // Mới - Chờ xác nhận (Blue)
    11: 'status-conf',         // Đã xác nhận - Chờ hàng về (Indigo)
    12: 'status-wh',           // Hàng về kho - Chờ đóng gói (Orange)
    14: 'status-transmit',     // Đang vận chuyển (Primary)
    15: 'status-done',         // Hoàn thành (Success)
    16: 'status-return',       // Trả về (Purple)
    17: 'status-reserve',      // Chờ lưu kho (Teal)
    18: 'status-cancel',       // Hủy (Red)
    19: 'status-update',       // Phí thay đổi (Pink)
    20: 'status-loss',         // Thất lạc (Dark)
    21: 'status-reimburse',    // Bồi thường (Yellow)
  };
  return map[id] || 'status-default';
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.error('Lỗi khi copy: ', err);
  }
};

const onClickCancel = () => {
  isConfirmOrder.value = true
  typeConfirm.value = 'cancel'
};
const onBuyTracking = () => {
  isConfirmOrder.value = true
  typeConfirm.value = 'buy tracking'
};
const onPay = () => {
  isConfirmOrder.value = true
  typeConfirm.value = 'payment'
};
const onImport = () => {

  isImportOrder.value = true
};
const refresh = () => {
  isConfirmOrder.value = false;
  typeConfirm.value = '';
  selectedIds.value = [];
  if (tableRef.value) {
    tableRef.value.clearSelection();
  }
  fetchData();
}
const confirmCancel = async () => {
  if (selectedIds.value.length === 0) return toaster.error("Bản ghi không được để trống");
  try {
    let res = await changeStatusApi({ ids: selectedIds.value, status: "order-destroy" });

    if (res.success) {
      toaster.success("Thành công!");
      refresh();
    } else {
      toaster.error(res.error || "Thao tác thất bại");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  }
};

const onPrintLabel = async () => {
    if (selectedIds.value.length === 0) return;
    const ids = selectedIds.value.join(',');
    const url = router.resolve({
        name: 'printOrder',
        params: { ids }
    }).href;
    window.open(url, '_blank');
};


const confirmModal = async (type) => {
  if (type === 'cancel') await confirmCancel();
  else if (type === 'buy tracking') await confirmBuyTracking();
  else if (type === 'payment') await confirmPayment();
};

const confirmBatchStatus = async (newStatus) => {
  if (selectedIds.value.length === 0) return;

  loading.value = true;
  isChangeStatusOpen.value = false;

  try {
    // Gọi API change-status hàng loạt
    // Dùng key 'status' hoặc 'status_id' tùy theo mapping của Backend
    let res = await changeStatusApi({
      ids: selectedIds.value,
      status: newStatus
    });

    if (res.success) {
      toaster.success(`Đã chuyển ${selectedIds.value.length} đơn hàng sang trạng thái mới!`);
      refresh();
    } else {
      toaster.error(res.message || "Có lỗi xảy ra khi đổi trạng thái");
    }
  } catch (err) {
    console.error("Change status error:", err);
    toaster.error("Không thể kết nối đến máy chủ");
  } finally {
    loading.value = false;
  }
};

const confirmPayment = async () => {
  if (selectedIds.value.length === 0) return toaster.error("Bản ghi không được để trống");
  if (isPaying.value) return;
  isPaying.value = true;
  try {
    let res = await payOrdersApi({ ids: selectedIds.value });

    if (res.success) {
      toaster.success("Thanh toán thành công!");
      refresh();
    } else {
      toaster.error(res.error || "Thanh toán thất bại");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  } finally {
    isPaying.value = false;
  }
};

const resyncOrderPayment = async (item) => {
  const remainFee = Number(item.remain_fee || 0);
  if (!isAdmin.value || remainFee >= -0.01 || resyncingOrderId.value !== null) return;

  const netCredit = Math.abs(remainFee).toLocaleString('vi-VN');
  const confirmed = window.confirm(
    `Đơn ${item.code} đang thừa ${netCredit} đ. Tiếp tục đồng bộ sẽ hoàn các khoản thanh toán cũ vào số dư Vio và thu lại theo phí hiện tại, cộng ròng ${netCredit} đ vào số dư của khách.`
  );
  if (!confirmed) return;

  resyncingOrderId.value = item.id;
  try {
    const res = await resyncOrderPaymentApi(item.id);
    if (!res.success) {
      toaster.error(res.error || 'Không thể đồng bộ thanh toán');
      return;
    }

    const result = res.data?.data;
    if (result?.status === 'already_synced') {
      toaster.success('Thanh toán đơn hàng đã khớp với phí hiện tại.');
    } else {
      const refundAmount = Number(result?.refund_amount ?? remainFee).toLocaleString('vi-VN');
      toaster.success(`Đã đồng bộ. Số dư Vio của khách tăng ròng ${refundAmount} đ.`);
    }
    await fetchData();
  } catch (err) {
    toaster.error(err.message || 'Không thể đồng bộ thanh toán');
  } finally {
    resyncingOrderId.value = null;
  }
};

const confirmBuyTracking = async () => {
  if (selectedIds.value.length === 0) return toaster.error("Bản ghi không được để trống");
  try {
    let res = await buyTrackingApi({ ids: selectedIds.value });

    if (res.success) {
      toaster.success("Thành công!");
      refresh();
    } else {
      toaster.error(res.error || "Thao tác thất bại");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  }
};



const fetchData = async (resetPage = false) => {
  if (resetPage) {
    allParams.page = 1;
  }

  // Clear lựa chọn cũ khi load lại data (search, next page)
  selectedIds.value = [];
  if (tableRef.value) {
    tableRef.value.clearSelection();
  }

  loading.value = true;

  try {
    const params = { ...allParams };
    const res = await fetchOrdersApi(params);

    if (res.success && res.data) {
      tableData.value = res.data.data || [];
      rowOffset.value = (params.page - 1) * params.limit;

      total.value = res.data.meta?.pagination?.total || 0;
    } else {
      toaster.error(res.error || "Không thể tải dữ liệu");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  if (allParams.page === 1) {
    fetchData();
  } else {
    allParams.page = 1;
  }
};

const handleReset = () => {
  allParams.limit = 10;
  allParams.search = '';
  allParams.searchFields = '';
  allParams.orderBy = 'id';
  allParams.sortedBy = 'desc';
  allParams.searchJoin = 'AND';
  allParams.include = 'customer,order_items,fees,expenses';

  if (allParams.page === 1) {
    fetchData();
  } else {
    allParams.page = 1;
  }
};

const handleSort = ({ key, order }) => {
  allParams.sortedBy = key;
  allParams.order = order;
  fetchData();
};

const handleSelection = (ids) => {
  selectedIds.value = ids;
  console.log("IDs đã chọn:", selectedIds.value);
};

const totalUnpaidSelected = computed(() => {
  let total = 0;
  selectedIds.value.forEach(id => {
    const item = tableData.value.find(order => order.id === id);
    if (item && item.remain_fee > 0) {
      total += Number(item.remain_fee);
    }
  });
  return total;
});

watch(() => [allParams.page, allParams.limit], () => {
  fetchData();
});

onMounted(async () => {
  try {
    await loadCurrentUser();
    const res = await getServicesApi({ limit: -1, page: 1 });
    const resCountry = await getCountriesApi({ limit: -1, page: 1 });

    const resStatus = await getStatusesApi({ limit: -1, page: 1 });
    if (res.success && res.data) {
        services.value = res.data.data;
    }
    if (resCountry.success && resCountry.data) {
        countries.value = resCountry.data.data;
    }
    if (isAdmin.value) {
      const resSales = await getUsersApi({ search: 'role_id:5', limit: 1000, orderBy: 'name', sortedBy: 'asc' });
      if (resSales.success && resSales.data) {
        sales.value = resSales.data.data;
      }
    }
    if (resStatus.success && resStatus.data) {
      const allData = resStatus.data.data;
      const filteredOrder = allData.filter(item => item.scope === 'order');
      const filteredBuyTracking = allData.filter(item => item.scope === 'buy-tracking');

      console.log(filteredOrder);
      statusOrder.value = filteredOrder;
      statusBuyTracking.value = filteredBuyTracking;
    }
    await fetchData();
  } catch (err) {
    console.error("Lỗi khởi tạo màn hình:", err);
  }
});

const payItem = (item) => {
  selectedIds.value = [item.id];
  isConfirmOrder.value = true;
  typeConfirm.value = 'payment';
};

const printItem = (item) => {
  selectedIds.value = [item.id];
  onPrintLabel();
};

const viewDetail = (item) => {

  selectedOrder.value = item;
  isOrderDetailOpen.value = true;
};

const editItem = (item) => {
  editingOrderId.value = item.id;
  isOrderEditOpen.value = true;
};
</script>

<style scoped>
.filter-section .left-filter {
  max-height: 400px;
  overflow-y: auto;
  z-index: 99 !important;
}
.product-wrapper {
  padding-bottom: 50px;
}
.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
}

/* Color Mapping for Statuses */
.status-new { background-color: #e0f2fe; color: #0369a1; }       /* Blue */
.status-conf { background-color: #e0e7ff; color: #4338ca; }      /* Indigo */
.status-wh { background-color: #ffedd5; color: #c2410c; }        /* Orange */
.status-transmit { background-color: #dbeafe; color: #1d4ed8; }  /* Primary Blue */
.status-done { background-color: #d1fae5; color: #047857; }      /* Emerald/Green */
.status-return { background-color: #f3e8ff; color: #7e22ce; }    /* Purple */
.status-reserve { background-color: #ccfbf1; color: #0f766e; }   /* Teal */
.status-cancel { background-color: #fee2e2; color: #b91c1c; }    /* Red */
.status-update { background-color: #fce7f3; color: #be185d; }    /* Pink */
.status-loss { background-color: #111827; color: #ffffff; }      /* Dark/Black */
.status-reimburse { background-color: #fef9c3; color: #a16207; } /* Yellow/Gold */
.status-default { background-color: #f9fafb; color: #6b7280; }   /* Light Gray */

.table-tools .btn {
  padding-left: 8px;
  padding-right: 8px;
  font-size: 0.75rem;
  white-space: nowrap;
}
</style>
